import { useEffect, useState } from "react";
import { ISidebarItem } from "../../../../../components/common/sidebar/SidebarItem";
import { ArrowUpDown, ChevronDown, Globe2 } from "lucide-react";
import { useCreativeLibraryStore } from "@/screens/creative-library/store";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useParams } from "react-router-dom";
import { AdCreative, PublishAdCreative } from "@/screens/creative-library/types";
import PublishedTable from "./table/PublishedTable";
import UnpublishedTable from "./table/UnpublishedTable";
import ReportTitle from "@/screens/analysis/components/report/ReportTitle";
import { useGlobalStore } from "@/GlobalStore";
import PageLoader from "@/components/common/loaders/PageLoader";
import { RiEarthLine } from "@remixicon/react";
import { usePublishPagination } from "@/screens/creative-library/hooks/usePublishPagination";
import ExpiredTokenGuard from "@/router/guards/ExpiredTokenGuard";

export const PublishTab: ISidebarItem = {
  type: "tab",
  value: "publish",
  label: "Publish",
  icon: RiEarthLine,
  id: "publish",
  route: "publish",
};
type TabValue = "published" | "unpublished";
type SortOrder = "newest-first" | "oldest-first";

const PublishDashboard = () => {
  const { selectedSidebarItem, setSelectedSidebarItem } = useCreativeLibraryStore();
  const { sidebarCollapsed } = useGlobalStore();
  const [selectedTab, setSelectedTab] = useState<TabValue>("unpublished");
  const [sortOrder, setSortOrder] = useState<SortOrder>("newest-first");

  const { accountId } = useParams();

  // Pagination hooks for unpublished and published data
  const {
    data: unpublishedData,
    setData: setUnpublishedData,
    loading: unpublishedLoading,
    loadingMore: unpublishedLoadingMore,
    hasMore: unpublishedHasMore,
    fetchData: fetchUnpublishedData,
    handleLoadMore: handleLoadMoreUnpublished,
  } = usePublishPagination(accountId, "unpublished");

  const {
    data: publishedData,
    setData: setPublishedData,
    loading: publishedLoading,
    loadingMore: publishedLoadingMore,
    hasMore: publishedHasMore,
    fetchData: fetchPublishedData,
    handleLoadMore: handleLoadMorePublished,
  } = usePublishPagination(accountId, "published");

  // setting initial tab
  useEffect(() => {
    if (selectedSidebarItem?.id !== PublishTab.id) setSelectedSidebarItem(PublishTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initial fetch for both tabs
  useEffect(() => {
    void fetchUnpublishedData();
    void fetchPublishedData();
  }, [fetchUnpublishedData, fetchPublishedData]);

  const handleSort = (order: SortOrder) => {
    setSortOrder(order);
  };

  const displayText =
    sortOrder === "newest-first" ? "Latest to Oldest" : "Oldest to Latest";

  const publishedAdCreativeTableData = publishedData
    .map((publishAdCreative) => {
      const base = publishAdCreative.ad_creative;
      return {
        ad: publishAdCreative,
        publishingAsset: {
          campaign: {
            id: publishAdCreative.campaign_id ?? "",
            name: publishAdCreative.campaign_name ?? "",
          },
          adset: {
            id: publishAdCreative.adset_id ?? "",
            name: publishAdCreative.adset_name ?? "",
          },
        },
        primaryText: base?.creative?.titles ?? [],
        publishingDate: publishAdCreative.updated_at,
      };
    })
    .sort((a, b) => {
      const dateA = new Date(a.ad.created_at).getTime();
      const dateB = new Date(b.ad.created_at).getTime();
      return sortOrder === "newest-first" ? dateB - dateA : dateA - dateB;
    });

  const unPublishedAdCreativeTableData = unpublishedData
    .map((publishAdCreative) => {
      const base = publishAdCreative.ad_creative;
      return {
        ad: publishAdCreative,
        publishingAsset: {
          campaign: {
            id: publishAdCreative.campaign_id ?? "",
            name: publishAdCreative.campaign_name ?? "",
          },
          adset: {
            id: publishAdCreative.adset_id ?? "",
            name: publishAdCreative.adset_name ?? "",
          },
        },
        primaryText: base?.creative?.titles ?? [],
      };
    })
    .sort((a, b) => {
      const dateA = new Date(a.ad.created_at).getTime();
      const dateB = new Date(b.ad.created_at).getTime();
      return sortOrder === "newest-first" ? dateB - dateA : dateA - dateB;
    });

  const handleUpdateUnpublishedAdCreative = (
    updatedPublishAdCreative: PublishAdCreative,
  ) => {
    setUnpublishedData((prev) =>
      prev.map((publishAdCreative) =>
        publishAdCreative.id === updatedPublishAdCreative.id
          ? updatedPublishAdCreative
          : publishAdCreative,
      ),
    );
  };

  const handlePollingAdCreativesUpdate = (
    adCreatives: AdCreative[],
    publishCreativeId: string,
  ) => {
    setUnpublishedData((prev) =>
      prev.map((publishAdCreative) => {
        if (publishAdCreative.id === publishCreativeId) {
          return {
            ...publishAdCreative,
            ad_creatives: adCreatives,
          };
        }
        return publishAdCreative;
      }),
    );
  };

  const handleOnCancelAdCreatives = (canceledPublishAdCreativeId: string) => {
    setUnpublishedData((prev) =>
      prev.filter(
        (publishAdCreative) => publishAdCreative.id !== canceledPublishAdCreativeId,
      ),
    );
  };

  const handleOnApproveAdCreative = (approvedPublishAdCreative: PublishAdCreative) => {
    // Remove from unpublished list
    setUnpublishedData((prev) =>
      prev.filter(
        (publishAdCreative) => publishAdCreative.id !== approvedPublishAdCreative.id,
      ),
    );
    // Add to published list
    setPublishedData((prev) => [
      { ...approvedPublishAdCreative, state: "published" },
      ...prev,
    ]);
  };

  const loading = unpublishedLoading || publishedLoading;

  return (
    <ExpiredTokenGuard
      description={(account) => (
        <>
          Publishing is paused for{" "}
          <span className="text-brand-primary-800 font-medium">{account?.name}</span>.
          Reconnect <span className="lowercase">{account?.platform}</span> to resume.
        </>
      )}
    >
      <div className="flex flex-col w-full h-full">
        {/* sorting */}
        <div className="flex gap-2 pt-[8px] pb-2 pl-[9px]">
          {sidebarCollapsed && <div className="h-[32px] w-[32px] shrink-0"></div>}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="sq-outline" className="flex items-center gap-2 w-[154px]">
                <ArrowUpDown
                  size={14}
                  className="text-brand-primary-400"
                  strokeWidth={1.8}
                />
                <span className="font-['Helvetica_Neue'] text-[11px] font-normal leading-[19.16px] tracking-[0.02em] text-brand-primary-800">
                  {displayText}
                </span>
                <ChevronDown
                  size={14}
                  className="text-brand-primary-400"
                  strokeWidth={1.8}
                />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[154px] !rounded-[8px]">
              <DropdownMenuItem
                onClick={() => handleSort("newest-first")}
                className="flex items-center px-4 py-2 text-[12px] font-normal leading-[19.16px] tracking-[0.02em] text-brand-primary-new hover:bg-[#F7F7F3] rounded-[4px]"
              >
                Latest to Oldest
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleSort("oldest-first")}
                className="flex items-center px-4 py-2 text-[12px] font-normal leading-[19.16px] tracking-[0.02em] text-brand-primary-new hover:bg-[#F7F7F3] rounded-[4px]"
              >
                Oldest to Latest
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        {/* Tab Name */}
        <div className="flex items-center gap-2 mt-5 pl-[9px]">
          <ReportTitle
            title={PublishTab.label}
            icon={<PublishTab.icon size={16} className="text-brand-orange" />}
            iconBackgroundColor="#FF45000F"
          />
        </div>
        <Separator className="mt-5" />
        {loading ? (
          <div className="flex items-center justify-center h-[80%] w-full">
            <PageLoader />
          </div>
        ) : (
          <Tabs
            defaultValue="unpublished"
            value={selectedTab}
            onValueChange={(value) => setSelectedTab(value as TabValue)}
            className="mx-[9px] my-8 border-t-[1px] border-solid border-border-gray rounded-[12px] h-screen"
          >
            <TabsList className="h-10 border-x-[1px] border-solid border-border-gray w-full bg-white flex justify-start p-0 rounded-none rounded-t-[12px] relative after:content-[''] after:absolute after:left-0 after:right-0 after:bottom-0 after:h-[1px] after:bg-[#E7E9EF] after:z-0">
              <TabsTrigger
                value="unpublished"
                className="h-10 py-2 px-4 pb-1 rounded-none font-normal text-[12px] leading-[19.16px] tracking-[0.02em] text-[#35467A] z-10 border-b-[1.5px] border-transparent
            data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-[1.5px] data-[state=active]:border-[#FF4500]
            data-[state=active]:text-[#FF4500]"
              >
                Unpublished
              </TabsTrigger>
              <TabsTrigger
                value="published"
                className="h-10 py-2 px-4 pb-1 rounded-none font-normal text-[12px] leading-[19.16px] tracking-[0.02em] text-[#35467A] z-10 border-b-[1.5px] border-transparent
            data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-[1.5px] data-[state=active]:border-[#FF4500] 
            data-[state=active]:text-[#FF4500]"
              >
                <div className="flex items-center gap-1">
                  <Globe2 size={14} className="shrink-0 relative bottom-[2px]" />
                  <span>Published</span>
                </div>
              </TabsTrigger>
            </TabsList>
            <TabsContent value="unpublished" className="m-0 h-[calc(100%-40px)]">
              <UnpublishedTable
                data={unPublishedAdCreativeTableData}
                pollingCompleteAdCreativesCb={handlePollingAdCreativesUpdate}
                updatePublishAdCreative={handleUpdateUnpublishedAdCreative}
                onCancel={handleOnCancelAdCreatives}
                onApprove={handleOnApproveAdCreative}
                hasMore={unpublishedHasMore}
                isLoadingMore={unpublishedLoadingMore}
                onLoadMore={handleLoadMoreUnpublished}
              />
            </TabsContent>
            <TabsContent value="published" className="m-0 h-[calc(100%-40px)]">
              <PublishedTable
                data={publishedAdCreativeTableData}
                hasMore={publishedHasMore}
                isLoadingMore={publishedLoadingMore}
                onLoadMore={handleLoadMorePublished}
              />
            </TabsContent>
          </Tabs>
        )}
      </div>
    </ExpiredTokenGuard>
  );
};

export default PublishDashboard;
